/**
 * MODULE: _shared/frontend/lib/frontend/hooks/useTenantFiscalCalendar.ts
 * PURPOSE: Tenant fiscal year start month for quota and territory planning periods
 */

import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '@core/tenant';
import {
  fiscalPeriodFromDate,
  normalizeFiscalYearStartMonth,
  type FiscalCalendarConfig,
} from '@shared/lib/frontend/utils/fiscalCalendar';
import { resolveActiveTenantId, tenantIdForTenantApi } from './useTenantDefaultCurrency';

export const TENANT_FISCAL_QUERY_KEY = ['tenant', 'fiscal_year_start_month'] as const;

function bareId(id: string): string {
  return id.trim().replace(/^tnt_/, '');
}

export function useTenantFiscalCalendar(tenantIdOverride?: string | null) {
  const tenantId = resolveActiveTenantId(tenantIdOverride);

  const query = useQuery({
    queryKey: [...TENANT_FISCAL_QUERY_KEY, tenantId],
    queryFn: async (): Promise<FiscalCalendarConfig> => {
      if (!tenantId) return { fiscalYearStartMonth: 1 };
      const targetBare = bareId(tenantId);
      try {
        const tenants = await tenantApi.list();
        const match = tenants.find((t) => bareId(t.id) === targetBare);
        if (match && 'fiscal_year_start_month' in match) {
          return {
            fiscalYearStartMonth: normalizeFiscalYearStartMonth(
              (match as { fiscal_year_start_month?: number }).fiscal_year_start_month
            ),
          };
        }
        const tenant = await tenantApi.get(tenantIdForTenantApi(tenantId));
        return {
          fiscalYearStartMonth: normalizeFiscalYearStartMonth(
            (tenant as { fiscal_year_start_month?: number }).fiscal_year_start_month
          ),
        };
      } catch {
        return { fiscalYearStartMonth: 1 };
      }
    },
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const fiscalYearStartMonth = query.data?.fiscalYearStartMonth ?? 1;
  const config: FiscalCalendarConfig = { fiscalYearStartMonth };
  const nowPeriod = fiscalPeriodFromDate(new Date(), config);

  return {
    tenantId,
    fiscalYearStartMonth,
    fiscalCalendar: config,
    currentFiscalYear: nowPeriod.fiscalYear,
    currentFiscalQuarter: nowPeriod.fiscalQuarter,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
